/**
 * 版本管理工具函数
 * 用于本地版本号缓存和新版本检测
 */

// 版本号存储键名
const VERSION_STORAGE_KEY = 'app_version'
const VERSION_CHECK_INTERVAL = 1 * 60 * 1000 // 1分钟检查一次，提高检测频率

// 版本号接口
export interface VersionInfo {
  version: string
  buildTime: string
  checkTime: number
}

/**
 * 获取当前应用版本号
 * 从vite注入的全局变量读取
 */
export function getCurrentVersion(): string {
  // 从vite注入的全局变量读取版本号
  const version = (globalThis as any).__APP_VERSION__
  
  // 调试信息
  if (import.meta.env.DEV) {
    // console.log('获取版本号:', version)
  }
  
  // 如果版本号不存在，返回默认值
  if (!version || version === 'undefined' || version === 'null' || version === '' || version === null) {
    // console.warn('无法获取应用版本号，版本变量未定义')
    return '1.1.59'
  }
  
  return version
}

/**
 * 获取当前构建时间
 */
export function getCurrentBuildTime(): string {
  const buildTime = (globalThis as any).__APP_BUILD_TIME__
  
  // 如果构建时间不存在，返回当前时间
  if (!buildTime || buildTime === 'undefined' || buildTime === 'null' || buildTime === '' || buildTime === null) {
    // console.warn('无法获取构建时间，构建时间变量未定义')
    return new Date().toISOString()
  }
  
  return buildTime
}

/**
 * 从本地存储获取缓存的版本信息
 */
export function getCachedVersion(): VersionInfo | null {
  try {
    const cached = localStorage.getItem(VERSION_STORAGE_KEY)
    return cached ? JSON.parse(cached) : null
  } catch (error) {
    console.warn('读取版本缓存失败:', error)
    return null
  }
}

/**
 * 将版本信息保存到本地存储
 */
export function saveVersionToCache(versionInfo: VersionInfo): void {
  // try {
  //   localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(versionInfo))
  // } catch (error) {
  //   console.warn('保存版本缓存失败:', error)
  // }
}

/**
 * 检查是否需要更新版本
 */
export function checkVersionUpdate(): boolean {
  const currentVersion = getCurrentVersion()
  const cachedVersion = getCachedVersion()
  
  // 如果没有缓存版本，说明是首次访问
  if (!cachedVersion) {
    // 保存当前版本信息
    const versionInfo: VersionInfo = {
      version: currentVersion,
      buildTime: getCurrentBuildTime(),
      checkTime: Date.now()
    }
    saveVersionToCache(versionInfo)
    return false
  }
  
  // 比较版本号
  if (cachedVersion.version !== currentVersion) {
    return true
  }
  
  // 检查是否需要重新检查版本（避免频繁检查）
  const now = Date.now()
  if (now - cachedVersion.checkTime > VERSION_CHECK_INTERVAL) {
    // 更新检查时间
    const updatedVersionInfo: VersionInfo = {
      ...cachedVersion,
      checkTime: now
    }
    saveVersionToCache(updatedVersionInfo)
  }
  
  return false
}

/**
 * 更新本地版本缓存
 */
export function updateVersionCache(): void {
  const versionInfo: VersionInfo = {
    version: getCurrentVersion(),
    buildTime: getCurrentBuildTime(),
    checkTime: Date.now()
  }
  saveVersionToCache(versionInfo)
}

/**
 * 清除版本缓存
 */
export function clearVersionCache(): void {
  try {
    localStorage.removeItem(VERSION_STORAGE_KEY)
  } catch (error) {
    console.warn('清除版本缓存失败:', error)
  }
}

/**
 * 版本号比较函数
 * 支持语义化版本号比较
 */
export function compareVersions(version1: string, version2: string): number {
  const v1Parts = version1.split('.').map(Number)
  const v2Parts = version2.split('.').map(Number)
  
  const maxLength = Math.max(v1Parts.length, v2Parts.length)
  
  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Parts[i] || 0
    const v2Part = v2Parts[i] || 0
    
    if (v1Part > v2Part) return 1
    if (v1Part < v2Part) return -1
  }
  
  return 0
}

/**
 * 获取版本更新提示信息
 */
export function getVersionUpdateMessage(oldVersion: string, newVersion: string): string {
  const comparison = compareVersions(newVersion, oldVersion)
  
  if (comparison > 0) {
    return `检测到新版本 ${newVersion}，页面将自动刷新以获取最新功能`
  } else if (comparison < 0) {
    return `当前版本 ${newVersion} 低于缓存版本 ${oldVersion}，请检查版本信息`
  }
  
  return '版本检查完成'
}

/**
 * 强制刷新页面
 * 清除所有缓存并重新加载页面
 */
export function forceRefreshPage(): void {
  try {
    // 清除所有本地存储
    // localStorage.clear()
    localStorage.removeItem('currentModuleKind')
    sessionStorage.clear()
    
    // 清除所有缓存
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name)
        })
      })
    }
    
    // 强制刷新页面
    window.location.reload()
  } catch (error) {
    console.warn('强制刷新失败:', error)
    // 如果强制刷新失败，使用普通刷新
    window.location.reload()
  }
}

/**
 * 延迟强制刷新页面
 * 给用户一些时间看到提示信息
 */
export function delayedForceRefresh(delay: number = 3000): void {
  setTimeout(() => {
    forceRefreshPage()
  }, delay)
}
